// Shared move execution logic - builds Firestore update data
// Deduplicates capture handling, elimination messages, and game-over checks

import { findPlayerToEliminateBySquare, findPlayerToEliminateLegacy, getUpdatedPiecesAfterMove } from './pieceTracking';
import { PIECE_NAMES, PROMOTION_PIECE_NAMES } from './gameUtils';

// Heavy revert-snapshot fields stored on each move. The admin "revert last
// move" action restores from the LAST entry and can be used repeatedly (each
// revert exposes the next entry), so we retain the full snapshot on the most
// recent SNAPSHOT_RETENTION moves and strip it from older ones. This keeps the
// games doc under Firestore's 1 MB document limit (`previousPieces` is a full
// board snapshot, ~5-8 KB per move) while preserving deep consecutive reverts.
const HEAVY_HISTORY_FIELDS = ['previousPieces', 'previousWhiteTeam', 'previousBlackTeam'];
const SNAPSHOT_RETENTION = 25;

const appendTrimmedHistory = (prevHistory, newEntry) => {
  const all = (prevHistory || []).concat([newEntry]);
  const keepFrom = Math.max(0, all.length - SNAPSHOT_RETENTION);
  return all.map((entry, i) => {
    if (i >= keepFrom) return entry; // keep full snapshot on the most recent N
    const copy = { ...entry };
    for (const f of HEAVY_HISTORY_FIELDS) delete copy[f];
    return copy;
  });
};

// Build the common Firestore update data after a move is executed.
// Returns { updateData, chats } where chats is an array of chat docs
// to be written to the /games/{gameId}/chats subcollection (typically
// via writeBatch in the same commit as the parent update).
export const buildMoveUpdateData = ({
  game,
  playerTeam,
  move,          // chess.js move result
  sourceSquare,
  targetSquare,
  newFen,
  newTurn,
  chatMessage,
  isKingDirect = false,
  wasRandom = false,
  maxVotes = 0,
  hasKingRole = false,
}) => {
  const opponentTeamKey = playerTeam === 'white' ? 'black' : 'white';
  const teamFieldKey = playerTeam === 'white' ? 'whiteTeam' : 'blackTeam';

  const updateData = {
    fen: newFen,
    currentTurn: newTurn,
    moveHistory: appendTrimmedHistory(game.moveHistory, {
      from: sourceSquare,
      to: targetSquare,
      notation: move.san,
      votes: maxVotes,
      wasRandom,
      isKingDirect,
      timestamp: new Date().toISOString(),
      previousFen: game.fen,
      previousTurn: game.currentTurn,
      previousEliminatedPlayers: game.eliminatedPlayers || [],
      previousPieces: game.pieces || null,
      previousWhiteTeam: game.whiteTeam || null,
      previousBlackTeam: game.blackTeam || null
    }),
    'suggestedMoves.white': [],
    'suggestedMoves.black': [],
    'activePolls.white': null,
    'activePolls.black': null,
    'turnTimer.white': null,
    'turnTimer.black': null,
  };

  const chats = [{ ...chatMessage, team: playerTeam }];

  // Handle piece capture
  let capturedPieceId = null;
  if (move.captured) {
    const isEnPassant = move.flags && move.flags.includes('e');
    const capturedColor = playerTeam === 'white' ? 'b' : 'w';

    const captureResult = findPlayerToEliminateBySquare(
      game, targetSquare, isEnPassant, capturedColor, move.captured
    );

    if (captureResult.playerEmail) {
      const eliminatedPlayers = game.eliminatedPlayers || [];
      updateData.eliminatedPlayers = [...eliminatedPlayers, captureResult.playerEmail];
      capturedPieceId = captureResult.pieceId;

      const opponentTeam = playerTeam === 'white' ? game.blackTeam : game.whiteTeam;
      const eliminatedPlayer = opponentTeam?.find(p => p.email === captureResult.playerEmail);

      if (eliminatedPlayer && captureResult.piece) {
        const typeName = captureResult.piece.type.charAt(0).toUpperCase() + captureResult.piece.type.slice(1);
        chats.push({
          sender: 'SYSTEM',
          message: `${eliminatedPlayer.nickname}'s ${typeName} was captured${isEnPassant ? ' en passant' : ''}! They are now eliminated.`,
          timestamp: new Date().toISOString(),
          isElimination: true,
          team: opponentTeamKey,
        });
      }
    } else if (captureResult.piece) {
      capturedPieceId = captureResult.pieceId;
      console.warn(`Piece ${captureResult.pieceId} captured but no player found to eliminate`);
    } else {
      // Legacy fallback for old games
      console.warn('Falling back to legacy elimination method');
      const playerToEliminate = findPlayerToEliminateLegacy(
        game, move.captured, capturedColor, targetSquare
      );

      if (playerToEliminate) {
        const eliminatedPlayers = game.eliminatedPlayers || [];
        updateData.eliminatedPlayers = [...eliminatedPlayers, playerToEliminate];

        const opponentTeam = playerTeam === 'white' ? game.blackTeam : game.whiteTeam;
        const eliminatedPlayer = opponentTeam?.find(p => p.email === playerToEliminate);

        if (eliminatedPlayer) {
          chats.push({
            sender: 'SYSTEM',
            message: `${eliminatedPlayer.nickname}'s ${PIECE_NAMES[move.captured]} was captured! They are now eliminated.`,
            timestamp: new Date().toISOString(),
            isElimination: true,
            team: opponentTeamKey,
          });
        }
      }
    }
  }

  // Update pieces data
  const updatedPieces = getUpdatedPiecesAfterMove(
    game.pieces, sourceSquare, targetSquare, capturedPieceId
  );
  if (updatedPieces) {
    updateData.pieces = updatedPieces;
  }

  // Handle pawn promotion: convert the pawn piece itself (and its owning
  // player's role) to the promoted piece. No revival of eliminated players —
  // dead pieces stay dead.
  if (move.promotion) {
    const promotedTypeName = (PROMOTION_PIECE_NAMES[move.promotion] || 'Queen');
    const promotedTypeLower = promotedTypeName.toLowerCase();
    const targetLower = targetSquare.toLowerCase();
    const colorName = playerTeam === 'white' ? 'White' : 'Black';

    // Find the pawn piece that just moved (now sitting at the target square in pieces data)
    const piecesAfter = updateData.pieces;
    let promotedPieceId = null;
    if (piecesAfter) {
      const promotedPiece = Object.values(piecesAfter).find(p =>
        p.currentPosition === targetLower && !p.captured && p.type === 'pawn'
      );
      if (promotedPiece) {
        promotedPieceId = promotedPiece.id;
        piecesAfter[promotedPieceId] = {
          ...piecesAfter[promotedPieceId],
          type: promotedTypeLower,
          isPromoted: true,
          promotedFrom: 'pawn',
          promotedAt: new Date().toISOString()
        };
      }
    }

    // Update the player who owned that pawn so their role reflects the new piece.
    const teamArray = playerTeam === 'white' ? game.whiteTeam : game.blackTeam;
    if (teamArray && promotedPieceId) {
      const newRole = `${colorName} ${promotedTypeName} (${targetSquare.toUpperCase()})`;
      let promotedPlayerNickname = null;
      const updatedTeam = teamArray.map(p => {
        if (p.pieceId === promotedPieceId) {
          promotedPlayerNickname = p.nickname;
          return { ...p, role: newRole };
        }
        return p;
      });
      updateData[teamFieldKey] = updatedTeam;

      if (promotedPlayerNickname) {
        chats.push({
          sender: 'SYSTEM',
          message: `${promotedPlayerNickname}'s pawn was promoted to ${promotedTypeName} at ${targetSquare.toUpperCase()}!`,
          timestamp: new Date().toISOString(),
          isPromotion: true,
          team: playerTeam,
        });
      }
    }
  }

  return { updateData, chats };
};

// Build game-over update data if the game has ended
// Returns null if game is not over
export const buildGameOverUpdate = (chess, playerTeam) => {
  if (!chess.isGameOver()) return null;

  const teamName = playerTeam === 'white' ? 'White' : 'Black';
  let reason = '';

  if (chess.isCheckmate()) {
    reason = `Checkmate! ${teamName} wins!`;
  } else if (chess.isStalemate()) {
    reason = 'Stalemate!';
  } else if (chess.isDraw()) {
    reason = 'Draw!';
  }

  return {
    status: 'completed',
    completedAt: new Date().toISOString(),
    winner: chess.isCheckmate() ? playerTeam : null,
    endReason: reason
  };
};
